import { usePage } from "@inertiajs/react"
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import { csrfFetch } from "@/lib/csrf-fetch"
import type {
  ReportPagination,
  ReportSubscriptionData,
  ReportViewData,
  SharedProps,
} from "@/types"

import {
  COMMON_TIMEZONES,
  DAY_OF_MONTH_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  FORMAT_OPTIONS,
  FREQUENCY_OPTIONS,
  type Format,
  HOUR_OPTIONS,
  browserTimezone,
  dayOfMonthLabel,
  dayOfWeekLabel,
  formatsLabel,
  hourLabel,
  normalizeFormats,
} from "./schedule-options"

interface OrgUser {
  id: number
  name: string
  email: string
}

interface Props {
  reportKey: string
  reportTitle: string
  activeView?: ReportViewData | null
  mySubscription: ReportSubscriptionData | null
  subscriberCount?: number | null
  canSubscribeSelf?: boolean
  canManageSubscribers?: boolean
  /** False for a needs-review view; existing deliveries remain manageable. */
  canCreateSubscriptions?: boolean
  /** Explains a report-specific creation block while cadence stays editable. */
  creationBlockedMessage?: string
  orgUsers: OrgUser[]
  isDirty?: boolean
  open?: boolean
  onOpenChange?: (next: boolean) => void
  onSubscriberCountChange?: (count: number) => void
}

export interface ScheduleDraft {
  frequency: "daily" | "weekly" | "monthly"
  timezone: string
  send_hour: number
  send_day_of_week: number
  send_day_of_month: number
  formats: Format[]
}

interface SubscriptionMutationResponse {
  ok?: boolean
  error?: string
  subscription?: ReportSubscriptionData
}

export async function mutateSubscription(
  url: string,
  method: "POST" | "PATCH" | "DELETE",
  reportSubscription?: Record<string, unknown>,
): Promise<SubscriptionMutationResponse> {
  const response = await csrfFetch(url, {
    method,
    headers: { Accept: "application/json" },
    body: reportSubscription
      ? JSON.stringify({ report_subscription: reportSubscription })
      : undefined,
  })
  const body = (await response.json().catch(() => ({}))) as {
    ok?: boolean
    error?: string | { message?: string }
    subscription?: ReportSubscriptionData
  }
  if (!response.ok || body.ok === false) {
    const message =
      typeof body.error === "string"
        ? body.error
        : body.error?.message || "Could not update this subscription."
    throw new Error(message)
  }

  return { ok: body.ok, subscription: body.subscription }
}

export function subscriptionRemovalMethod(
  subscription: ReportSubscriptionData,
  currentUserId: number,
): "PATCH" | "DELETE" {
  const own = subscription.user_id === currentUserId
  return own && !subscription.review_required ? "PATCH" : "DELETE"
}

// Same wording the one-off Send dialog uses for the same mistake.
const NO_FORMAT_SELECTED = "Pick at least one format."

const defaultSchedule = (): ScheduleDraft => ({
  frequency: "daily",
  timezone: browserTimezone(),
  send_hour: 8,
  send_day_of_week: 1,
  send_day_of_month: 1,
  // CSV alone matches the column default and what every subscription delivered
  // before formats were selectable — a new subscriber shouldn't silently start
  // receiving attachments they didn't ask for.
  formats: ["csv"],
})

export function sortSubscriptions(
  subscriptions: ReportSubscriptionData[],
  currentUserId: number | null,
) {
  return [...subscriptions].sort((a, b) => {
    const aOwn = a.user_id === currentUserId ? 0 : 1
    const bOwn = b.user_id === currentUserId ? 0 : 1
    if (aOwn !== bOwn) return aOwn - bOwn
    return a.recipient_label.localeCompare(b.recipient_label)
  })
}

export function summarizeSchedule(sub: ReportSubscriptionData): string {
  const time = hourLabel(sub.send_hour)
  const attachments = formatsLabel(sub.formats ?? [])
  const suffix = attachments ? ` · ${attachments}` : ""
  if (sub.frequency === "weekly") {
    return `${dayOfWeekLabel(sub.send_day_of_week)} at ${time} · ${sub.timezone}${suffix}`
  }
  if (sub.frequency === "monthly") {
    return `${dayOfMonthLabel(sub.send_day_of_month)} of the month at ${time} · ${sub.timezone}${suffix}`
  }
  return `Daily at ${time} · ${sub.timezone}${suffix}`
}

function ScheduleFields({
  value,
  onChange,
  disabled,
  idPrefix,
  autoFocus = false,
}: {
  value: ScheduleDraft
  onChange: (next: ScheduleDraft) => void
  disabled?: boolean
  idPrefix: string
  autoFocus?: boolean
}) {
  const set = <K extends keyof ScheduleDraft>(key: K, next: ScheduleDraft[K]) =>
    onChange({ ...value, [key]: next })

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-frequency`}>Delivery frequency</Label>
          <Select
            value={value.frequency}
            onValueChange={(next) =>
              set("frequency", next as ScheduleDraft["frequency"])
            }
            disabled={disabled}
          >
            <SelectTrigger
              id={`${idPrefix}-frequency`}
              className="h-11"
              autoFocus={autoFocus}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FREQUENCY_OPTIONS.map((frequency) => (
                <SelectItem
                  key={frequency}
                  value={frequency}
                  className="min-h-11"
                >
                  {frequency.charAt(0).toUpperCase() + frequency.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-hour`}>Delivery time</Label>
          <Select
            value={String(value.send_hour)}
            onValueChange={(next) => set("send_hour", Number(next))}
            disabled={disabled}
          >
            <SelectTrigger id={`${idPrefix}-hour`} className="h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOUR_OPTIONS.map((hour) => (
                <SelectItem
                  key={hour.value}
                  value={String(hour.value)}
                  className="min-h-11"
                >
                  {hour.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {value.frequency === "weekly" && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-weekday`}>Day of week</Label>
          <Select
            value={String(value.send_day_of_week)}
            onValueChange={(next) => set("send_day_of_week", Number(next))}
            disabled={disabled}
          >
            <SelectTrigger id={`${idPrefix}-weekday`} className="h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAY_OF_WEEK_OPTIONS.map((day) => (
                <SelectItem
                  key={day.value}
                  value={String(day.value)}
                  className="min-h-11"
                >
                  {day.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {value.frequency === "monthly" && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-monthday`}>Day of month</Label>
          <Select
            value={String(value.send_day_of_month)}
            onValueChange={(next) => set("send_day_of_month", Number(next))}
            disabled={disabled}
          >
            <SelectTrigger id={`${idPrefix}-monthday`} className="h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DAY_OF_MONTH_OPTIONS.map((day) => (
                <SelectItem
                  key={day.value}
                  value={String(day.value)}
                  className="min-h-11"
                >
                  {day.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {value.send_day_of_month > 28 && (
            <p className="text-muted-foreground text-xs">
              Shorter months use their last day.
            </p>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-timezone`}>Timezone</Label>
        <Select
          value={value.timezone}
          onValueChange={(next) => set("timezone", next)}
          disabled={disabled}
        >
          <SelectTrigger id={`${idPrefix}-timezone`} className="h-11">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {!(COMMON_TIMEZONES as readonly string[]).includes(
              value.timezone,
            ) && (
              <SelectItem value={value.timezone} className="min-h-11">
                {value.timezone}
              </SelectItem>
            )}
            {COMMON_TIMEZONES.map((timezone) => (
              <SelectItem key={timezone} value={timezone} className="min-h-11">
                {timezone}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>Attach</Label>
        <div className="flex gap-4">
          {FORMAT_OPTIONS.map((format) => (
            <label
              key={format}
              className="flex min-h-11 cursor-pointer items-center gap-1.5 text-sm"
            >
              <Checkbox
                checked={value.formats.includes(format)}
                disabled={disabled}
                onCheckedChange={(checked) =>
                  set(
                    "formats",
                    normalizeFormats(
                      checked
                        ? [...value.formats, format]
                        : value.formats.filter((f) => f !== format),
                    ),
                  )
                }
              />
              {format.toUpperCase()}
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

function RecipientForm({
  users,
  submitting,
  onCancel,
  onSubmit,
}: {
  users: OrgUser[]
  submitting: boolean
  onCancel: () => void
  onSubmit: (
    recipient: { user_id?: number; email?: string },
    schedule: ScheduleDraft,
  ) => void
}) {
  const [kind, setKind] = useState<"member" | "external">("member")
  const [userId, setUserId] = useState("")
  const [email, setEmail] = useState("")
  const [schedule, setSchedule] = useState(defaultSchedule)
  const valid =
    kind === "member" ? Boolean(userId) : /.+@.+\..+/.test(email.trim())

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div>
        <h3 className="font-medium">Add subscriber</h3>
        <p className="text-muted-foreground text-xs">
          Their delivery follows this report view.
        </p>
      </div>
      <div
        className="grid grid-cols-2 rounded-md border p-1"
        role="group"
        aria-label="Recipient type"
      >
        {(["member", "external"] as const).map((option) => (
          <Button
            key={option}
            type="button"
            variant={kind === option ? "secondary" : "ghost"}
            className="h-11"
            aria-pressed={kind === option}
            onClick={() => setKind(option)}
            autoFocus={option === "member"}
          >
            {option === "member" ? "Organization member" : "External email"}
          </Button>
        ))}
      </div>
      {kind === "member" ? (
        <div className="space-y-1.5">
          <Label htmlFor="new-subscriber-user">Organization member</Label>
          <Select
            value={userId}
            onValueChange={setUserId}
            disabled={submitting}
          >
            <SelectTrigger id="new-subscriber-user" className="h-11">
              <SelectValue placeholder="Choose a member" />
            </SelectTrigger>
            <SelectContent>
              {users.map((user) => (
                <SelectItem
                  key={user.id}
                  value={String(user.id)}
                  className="min-h-11"
                >
                  {user.name} ({user.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="new-subscriber-email">Email address</Label>
          <Input
            id="new-subscriber-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="recipient@example.com"
            className="h-11"
            disabled={submitting}
          />
        </div>
      )}
      <ScheduleFields
        value={schedule}
        onChange={setSchedule}
        disabled={submitting}
        idPrefix="new-subscriber"
      />
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button
          variant="ghost"
          className="h-11"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          className="h-11"
          disabled={!valid || submitting}
          onClick={() =>
            onSubmit(
              kind === "member"
                ? { user_id: Number(userId) }
                : { email: email.trim() },
              schedule,
            )
          }
        >
          Add subscriber
        </Button>
      </div>
    </div>
  )
}

export default function SubscriptionsDialog({
  reportKey,
  reportTitle,
  activeView,
  mySubscription,
  subscriberCount,
  canSubscribeSelf = true,
  canManageSubscribers,
  canCreateSubscriptions = true,
  creationBlockedMessage,
  orgUsers,
  isDirty = false,
  open: controlledOpen,
  onOpenChange: controlledOnChange,
  onSubscriberCountChange,
}: Props) {
  const routes = useOrgRoutes()
  const subscriptionsPath = routes.reportSubscriptionsPath()
  const currentUserId = usePage<SharedProps>().props.auth.user.id
  const controlled = controlledOpen !== undefined
  const [localOpen, setLocalOpen] = useState(false)
  const open = controlled ? Boolean(controlledOpen) : localOpen
  const setOpen = (next: boolean) =>
    controlled ? controlledOnChange?.(next) : setLocalOpen(next)
  const resolvedCanManageSubscribers = Boolean(canManageSubscribers)
  const [subscriptions, setSubscriptions] = useState<ReportSubscriptionData[]>(
    mySubscription ? [mySubscription] : [],
  )
  const [pagination, setPagination] = useState<ReportPagination | null>(null)
  const [page, setPage] = useState(1)
  const [reloadNonce, setReloadNonce] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mutationError, setMutationError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)
  const [selfDraft, setSelfDraft] = useState(defaultSchedule)
  const [editDraft, setEditDraft] = useState<ScheduleDraft | null>(null)
  const focusLater = (id: string) => {
    window.setTimeout(() => document.getElementById(id)?.focus())
  }
  const resolvedSubscriberCount =
    pagination?.enabled_total ?? subscriberCount ?? 0

  const viewName = activeView?.name ?? "Default"
  const canCreateWhileClean = !isDirty && canCreateSubscriptions
  const effectiveMySubscription =
    subscriptions.find((sub) => sub.user_id === currentUserId) ?? mySubscription
  const sortedSubscriptions = useMemo(
    () => sortSubscriptions(subscriptions, currentUserId),
    [subscriptions, currentUserId],
  )

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const params = new URLSearchParams({
          report_key: reportKey,
          report_view_id: activeView ? String(activeView.id) : "default",
          page: String(page),
        })
        const response = await csrfFetch(`${subscriptionsPath}?${params}`, {
          headers: { Accept: "application/json" },
        })
        const body = (await response.json().catch(() => ({}))) as {
          items?: ReportSubscriptionData[]
          subscriptions?: ReportSubscriptionData[]
          pagination?: ReportPagination
          error?: string
        }
        if (!response.ok)
          throw new Error(body.error || "Could not load subscribers.")
        if (!cancelled) {
          setSubscriptions(body.items ?? body.subscriptions ?? [])
          const nextPagination = body.pagination ?? null
          setPagination(nextPagination)
          if (typeof nextPagination?.enabled_total === "number") {
            onSubscriberCountChange?.(nextPagination.enabled_total)
          }
        }
      } catch (reason) {
        if (!cancelled) {
          setError(
            reason instanceof Error
              ? reason.message
              : "Could not load subscribers.",
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [
    activeView,
    onSubscriberCountChange,
    open,
    page,
    reloadNonce,
    reportKey,
    subscriptionsPath,
  ])

  const refreshSubscriptions = () => {
    // A mutation can remove the final row from a later page. Return to page 1
    // so the follow-up request cannot strand the dialog on an empty page.
    if (page === 1) setReloadNonce((current) => current + 1)
    else setPage(1)
  }

  const mutationFailed = (reason: unknown) => {
    const message =
      reason instanceof Error
        ? reason.message
        : "Could not update this subscription."
    setMutationError(message)
    toast.error(message)
  }

  const createSubscription = async (
    recipient: { user_id?: number; email?: string },
    schedule: ScheduleDraft,
  ) => {
    if (!canCreateWhileClean) return
    if (schedule.formats.length === 0) {
      toast.error(NO_FORMAT_SELECTED)
      return
    }
    const successFocusTarget = adding
      ? "add-subscriber-trigger"
      : "subscriptions-dialog-title"
    setSubmitting(true)
    setMutationError(null)
    try {
      await mutateSubscription(routes.reportSubscriptionsPath(), "POST", {
        report_key: reportKey,
        report_view_id: activeView?.id ?? null,
        ...recipient,
        ...schedule,
      })
      setAdding(false)
      focusLater(successFocusTarget)
      toast.success(`Subscribed to “${viewName}”.`)
      refreshSubscriptions()
    } catch (reason) {
      mutationFailed(reason)
    } finally {
      setSubmitting(false)
    }
  }

  const saveSubscription = async (sub: ReportSubscriptionData) => {
    if (!editDraft) return
    if (editDraft.formats.length === 0) {
      toast.error(NO_FORMAT_SELECTED)
      return
    }
    setSubmitting(true)
    setMutationError(null)
    try {
      await mutateSubscription(routes.reportSubscriptionPath(sub.id), "PATCH", {
        ...editDraft,
      })
      setEditingId(null)
      setEditDraft(null)
      focusLater("subscriptions-dialog-title")
      toast.success("Delivery updated.")
      refreshSubscriptions()
    } catch (reason) {
      mutationFailed(reason)
    } finally {
      setSubmitting(false)
    }
  }

  const removeSubscription = async (sub: ReportSubscriptionData) => {
    const own = sub.user_id === currentUserId
    const method = subscriptionRemovalMethod(sub, currentUserId)
    if (
      !window.confirm(
        own
          ? `Unsubscribe from “${viewName}”?`
          : `Remove ${sub.recipient_label} from “${viewName}”?`,
      )
    )
      return
    setSubmitting(true)
    setMutationError(null)
    try {
      if (method === "PATCH") {
        await mutateSubscription(
          routes.reportSubscriptionPath(sub.id),
          "PATCH",
          { enabled: false },
        )
        toast.success(`Unsubscribed from “${viewName}”.`)
      } else {
        await mutateSubscription(
          routes.reportSubscriptionPath(sub.id),
          "DELETE",
        )
        toast.success(own ? "Subscription removed." : "Subscriber removed.")
      }
      focusLater("subscriptions-dialog-title")
      refreshSubscriptions()
    } catch (reason) {
      mutationFailed(reason)
    } finally {
      setSubmitting(false)
    }
  }

  const resubscribe = async (sub: ReportSubscriptionData) => {
    if (sub.user_id !== currentUserId || !canCreateWhileClean) return
    setSubmitting(true)
    setMutationError(null)
    try {
      await mutateSubscription(routes.reportSubscriptionPath(sub.id), "PATCH", {
        enabled: true,
      })
      toast.success(`Subscribed to “${viewName}”.`)
      focusLater("subscriptions-dialog-title")
      refreshSubscriptions()
    } catch (reason) {
      mutationFailed(reason)
    } finally {
      setSubmitting(false)
    }
  }

  const triggerLabel = resolvedCanManageSubscribers
    ? `Subscribers (${resolvedSubscriberCount})`
    : effectiveMySubscription
      ? "Manage subscription"
      : canCreateSubscriptions
        ? "Subscribe"
        : "Subscriptions"

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          setAdding(false)
          setEditingId(null)
          setEditDraft(null)
          setPage(1)
          setMutationError(null)
        }
      }}
    >
      {!controlled && (
        <DialogTrigger asChild>
          <Button
            variant={effectiveMySubscription?.enabled ? "secondary" : "outline"}
            className="h-11"
          >
            <Bell className="mr-2 size-4" />
            {triggerLabel}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="grid max-h-[90vh] max-w-[calc(100%_-_2rem)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden p-0 **:data-[slot=dialog-close]:flex **:data-[slot=dialog-close]:size-11 **:data-[slot=dialog-close]:items-center **:data-[slot=dialog-close]:justify-center sm:max-w-2xl">
        <DialogHeader className="border-b px-5 py-4 sm:px-6">
          <DialogTitle>
            <span id="subscriptions-dialog-title" tabIndex={-1}>
              {resolvedCanManageSubscribers
                ? "Subscribers"
                : "Report subscription"}
            </span>
          </DialogTitle>
          <DialogDescription>
            {reportTitle} · {viewName}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 space-y-4 overflow-y-auto px-5 py-4 sm:px-6">
          {(isDirty || !canCreateSubscriptions) && (
            <div
              role="status"
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            >
              {creationBlockedMessage ??
                (canCreateSubscriptions
                  ? "Save or reset your changes before adding a subscription."
                  : "Repair this organization view before adding a subscription.")}{" "}
              Existing delivery settings can still be managed.
            </div>
          )}

          {mutationError && (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800"
            >
              {mutationError}
            </div>
          )}

          {loading && (
            <div
              className="text-muted-foreground flex min-h-28 items-center justify-center gap-2 text-sm"
              role="status"
            >
              <Loader2 className="size-4 animate-spin" /> Loading subscribers…
            </div>
          )}
          {!loading && error && (
            <div
              role="alert"
              className="space-y-3 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800"
            >
              <p>{error}</p>
              <Button
                variant="outline"
                className="h-11"
                onClick={() => setReloadNonce((current) => current + 1)}
              >
                Try again
              </Button>
            </div>
          )}

          {!loading &&
            !error &&
            sortedSubscriptions.length === 0 &&
            !canSubscribeSelf && (
              <p className="text-muted-foreground rounded-md border border-dashed p-6 text-center text-sm">
                No one is subscribed to this view.
              </p>
            )}

          {!loading && !error && sortedSubscriptions.length > 0 && (
            <div className="space-y-2" aria-live="polite">
              {sortedSubscriptions.map((sub) => {
                const own = sub.user_id === currentUserId
                const canEdit = sub.capabilities
                  ? sub.capabilities.update
                  : own || resolvedCanManageSubscribers
                const canRemove = sub.capabilities
                  ? sub.capabilities.remove || sub.capabilities.unsubscribe
                  : own || resolvedCanManageSubscribers
                const editing = editingId === sub.id
                return (
                  <div key={sub.id} className="rounded-lg border p-3 sm:p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                          <span className="min-w-0 break-all">
                            {sub.recipient_label}
                          </span>
                          {own && <Badge variant="secondary">You</Badge>}
                          {sub.review_required ? (
                            <Badge variant="outline">Needs review</Badge>
                          ) : (
                            !sub.enabled && (
                              <Badge variant="outline">Unsubscribed</Badge>
                            )
                          )}
                        </div>
                        <p className="text-muted-foreground mt-1 text-xs">
                          {summarizeSchedule(sub)}
                        </p>
                        {sub.last_sent_at && (
                          <p className="text-muted-foreground mt-1 text-xs">
                            Last sent{" "}
                            {new Date(sub.last_sent_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {!editing && (
                        <div className="flex shrink-0 gap-1">
                          {canEdit && sub.enabled && (
                            <Button
                              id={`edit-subscription-${sub.id}`}
                              variant="ghost"
                              size="icon"
                              className="size-11"
                              aria-label={`Edit delivery for ${sub.recipient_label}`}
                              disabled={submitting}
                              onClick={() => {
                                setEditingId(sub.id)
                                setEditDraft({
                                  frequency: sub.frequency,
                                  timezone: sub.timezone,
                                  send_hour: sub.send_hour,
                                  send_day_of_week: sub.send_day_of_week ?? 1,
                                  send_day_of_month: sub.send_day_of_month ?? 1,
                                  // A tenant that predates the formats column
                                  // reports none; it still delivers CSV.
                                  formats: normalizeFormats(
                                    sub.formats ?? ["csv"],
                                  ),
                                })
                              }}
                            >
                              <Pencil className="size-4" />
                            </Button>
                          )}
                          {canRemove &&
                            (sub.enabled || sub.review_required) && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="hover:text-destructive size-11"
                                aria-label={
                                  sub.review_required
                                    ? "Remove subscription needing review"
                                    : own
                                      ? "Unsubscribe"
                                      : `Remove ${sub.recipient_label}`
                                }
                                disabled={submitting}
                                onClick={() => removeSubscription(sub)}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            )}
                          {own && !sub.enabled && !sub.review_required && (
                            <Button
                              className="h-11"
                              variant="outline"
                              disabled={submitting || !canCreateWhileClean}
                              onClick={() => resubscribe(sub)}
                            >
                              Subscribe again
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                    {editing && editDraft && (
                      <div className="mt-4 space-y-4 border-t pt-4">
                        <ScheduleFields
                          value={editDraft}
                          onChange={setEditDraft}
                          disabled={submitting}
                          idPrefix={`subscription-${sub.id}`}
                          autoFocus
                        />
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                          <Button
                            variant="ghost"
                            className="h-11"
                            disabled={submitting}
                            onClick={() => {
                              setEditingId(null)
                              focusLater(`edit-subscription-${sub.id}`)
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            className="h-11"
                            disabled={submitting}
                            onClick={() => saveSubscription(sub)}
                          >
                            Save delivery
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {!loading && !error && pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="outline"
                size="icon"
                className="size-11"
                disabled={pagination.page <= 1 || loading}
                onClick={() =>
                  setPage(pagination.previous ?? pagination.page - 1)
                }
                aria-label="Previous subscribers page"
              >
                <ChevronLeft className="size-4" />
              </Button>
              <span className="text-muted-foreground text-sm">
                Page {pagination.page} of {pagination.pages}
              </span>
              <Button
                variant="outline"
                size="icon"
                className="size-11"
                disabled={pagination.page >= pagination.pages || loading}
                onClick={() => setPage(pagination.next ?? pagination.page + 1)}
                aria-label="Next subscribers page"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>
          )}

          {!effectiveMySubscription && canSubscribeSelf && !adding && (
            <div className="space-y-4 rounded-lg border p-4">
              <div>
                <h3 className="font-medium">Subscribe to “{viewName}”</h3>
                <p className="text-muted-foreground text-sm">
                  {activeView
                    ? "Future changes to this organization view apply to later deliveries."
                    : "Default follows this report’s documented current settings and relative date window."}
                </p>
              </div>
              <ScheduleFields
                value={selfDraft}
                onChange={setSelfDraft}
                disabled={submitting || !canCreateWhileClean}
                idPrefix="my-subscription"
              />
              <Button
                className="h-11 w-full sm:w-auto"
                disabled={submitting || !canCreateWhileClean}
                onClick={() =>
                  createSubscription({ user_id: currentUserId }, selfDraft)
                }
              >
                {submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
                Subscribe
              </Button>
            </div>
          )}

          {adding && (
            <RecipientForm
              users={orgUsers}
              submitting={submitting}
              onCancel={() => {
                setAdding(false)
                focusLater("add-subscriber-trigger")
              }}
              onSubmit={createSubscription}
            />
          )}
        </div>

        <DialogFooter className="border-t px-5 py-4 sm:px-6">
          {resolvedCanManageSubscribers && !adding && (
            <Button
              id="add-subscriber-trigger"
              variant="outline"
              className="h-11"
              disabled={submitting || !canCreateWhileClean}
              onClick={() => setAdding(true)}
            >
              <Plus className="mr-2 size-4" /> Add subscriber
            </Button>
          )}
          <Button
            variant="ghost"
            className="h-11"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
